export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

import { connectSocket } from './socket';
import { getRuntimeServerUrl, isNativeShell } from './runtimeConfig';
import { playSoundEvent } from './sound';

const DEFAULT_API_BASE = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE) || '/api/v1';

function getClientHeaders(): Record<string, string> {
  try {
    const isDesktopClient = typeof window !== 'undefined' && !!window.desktopApi;
    return {
      'X-Client': isDesktopClient ? 'desktop' : 'web',
      'X-Device-Platform': isDesktopClient ? 'desktop' : 'web',
      // Fetch/XHR request headers must stay ASCII-safe in browsers/Electron renderers.
      'X-Device-Name': isDesktopClient ? 'Desktop Client' : 'Web Browser',
    };
  } catch {
    return {};
  }
}

function getApiBase(): string {
  try {
    if (!isNativeShell()) return String(DEFAULT_API_BASE || '/api/v1');
    const serverUrl = getRuntimeServerUrl();
    if (!serverUrl) return String(DEFAULT_API_BASE || '/api/v1');
    return `${serverUrl}/api/v1`;
  } catch {
    return String(DEFAULT_API_BASE || '/api/v1');
  }
}

function buildApiUrl(pathOrUrl: string): string {
  const apiBase = getApiBase();
  const url = String(pathOrUrl || '');
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/v1/')) return url;
  if (url.startsWith('/uploads/')) return `${apiBase}${url}`;
  if (url.startsWith('/downloads/')) return `${apiBase}${url}`;
  if (url.startsWith('/')) return `${apiBase}${url}`;
  return `${apiBase}/${url}`;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, Math.max(0, ms)));
}

const inflightGet = new Map<string, Promise<any>>();

async function downloadBlob(url: string, _retried = false): Promise<Blob> {
  const token = getToken();
  const finalUrl = buildApiUrl(url);
  const res = await fetch(finalUrl, {
    method: 'GET',
    credentials: 'include',
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      ...getClientHeaders(),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    if (res.status === 401 && !_retried) {
      const newToken = await doRefresh();
      if (newToken) return downloadBlob(url, true);
    }
    let body: any = null;
    let text = '';
    try {
      const ct = res.headers.get('content-type') || '';
      if (ct.includes('application/json')) body = await res.json();
      else text = await res.text();
    } catch {}
    if (body && typeof body === 'object') {
      const code = body.error || body.code;
      const msg = body.message || code || res.statusText;
      throw new HttpError(res.status, msg, { code, rid: body.rid, details: body.details, body });
    }
    throw new HttpError(res.status, text || res.statusText);
  }
  return res.blob();
}

async function triggerBrowserDownload(blob: Blob, filename: string): Promise<void> {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'file';
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => {
    try { URL.revokeObjectURL(url); } catch {}
  }, 0);
}

type UploadProgress = { loaded: number; total: number; percent: number | null };
type UploadTask<T> = { promise: Promise<T>; abort: () => void };

function xhrUploadTask<T>(path: string, files: File[], onProgress?: (progress: UploadProgress) => void): UploadTask<T> {
  const xhr = new XMLHttpRequest();

  const promise = new Promise<T>((resolve, reject) => {
    const token = getToken();
    const form = new FormData();
    files.forEach((f) => form.append('files', f));

    xhr.open('POST', buildApiUrl(path), true);
    xhr.withCredentials = true;
    const clientHeaders = getClientHeaders();
    for (const [header, value] of Object.entries(clientHeaders)) {
      try { xhr.setRequestHeader(header, value); } catch {}
    }
    if (token) {
      try { xhr.setRequestHeader('Authorization', `Bearer ${token}`); } catch {}
    }

    if (xhr.upload && typeof onProgress === 'function') {
      xhr.upload.onprogress = (e) => {
        const total = typeof e.total === 'number' ? e.total : 0;
        const loaded = typeof e.loaded === 'number' ? e.loaded : 0;
        const percent = total > 0 ? Math.round((loaded / total) * 100) : null;
        try { onProgress({ loaded, total, percent }); } catch {}
      };
    }

    xhr.onerror = () => {
      reject(new HttpError(0, 'network_error'));
    };

    xhr.onabort = () => {
      reject(new HttpError(0, 'upload_aborted', { code: 'upload_aborted' }));
    };

    xhr.onload = () => {
      const status = xhr.status || 0;
      const raw = xhr.responseText || '';
      if (status < 200 || status >= 300) {
        let body: any = null;
        try { body = raw ? JSON.parse(raw) : null; } catch {}
        if (body && typeof body === 'object') {
          const code = body.error || body.code;
          const msg = body.message || code || xhr.statusText || 'upload_failed';
          reject(new HttpError(status, msg, { code, rid: body.rid, details: body.details, body }));
          return;
        }
        reject(new HttpError(status, raw || xhr.statusText || 'upload_failed'));
        return;
      }
      try {
        resolve((raw ? JSON.parse(raw) : null) as T);
      } catch {
        // @ts-ignore
        resolve(raw as any as T);
      }
    };

    xhr.send(form);
  });

  return {
    promise,
    abort: () => {
      try { xhr.abort(); } catch {}
    },
  };
}

function xhrUpload<T>(path: string, files: File[], onProgress?: (progress: UploadProgress) => void): Promise<T> {
  return xhrUploadTask<T>(path, files, onProgress).promise;
}

function syncTokenCookie(token: string | null): void {
  try {
    const secure = typeof window !== 'undefined' && window.location && window.location.protocol === 'https:';
    if (token) {
      document.cookie = `al_jwt=${encodeURIComponent(token)}; Path=/; SameSite=Lax${secure ? '; Secure' : ''}`;
      return;
    }
    document.cookie = `al_jwt=; Path=/; SameSite=Lax${secure ? '; Secure' : ''}; Max-Age=0`;
    document.cookie = `al_jwt=; Path=/api/v1; SameSite=Lax${secure ? '; Secure' : ''}; Max-Age=0`;
  } catch {}
}

function getToken(): string | null {
  try {
    const token = localStorage.getItem('al_jwt');
    syncTokenCookie(token);
    return token;
  } catch {
    return null;
  }
}

async function clearAuthStorage(): Promise<void> {
  try { localStorage.removeItem('al_jwt'); } catch {}
  try { localStorage.removeItem('al_user'); } catch {}
  try { localStorage.removeItem('al_must_change_password'); } catch {}
  syncTokenCookie(null);
}

export class HttpError extends Error {
  status: number;
  code?: string;
  rid?: string;
  details?: any;
  body?: any;
  constructor(status: number, message: string, opts: Partial<{ code: string; rid: string; details: any; body: any }> = {}) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.code = opts.code;
    this.rid = opts.rid;
    this.details = opts.details;
    this.body = opts.body;
  }
}

let refreshing: Promise<string | null> | null = null;

async function doRefresh(): Promise<string | null> {
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await fetch(buildApiUrl('/auth/refresh'), {
          method: 'POST',
          credentials: 'include',
          headers: {
            ...getClientHeaders(),
          },
        });
        if (!res.ok) return null;
        const body = await res.json();
        const token = body?.token as string | undefined;
        if (token) {
          try { localStorage.setItem('al_jwt', token); } catch {}
          syncTokenCookie(token);
          try { connectSocket(); } catch {}
          return token;
        }
      } catch {}
      return null;
    })().finally(() => { setTimeout(() => { refreshing = null; }, 0); });
  }
  return refreshing;
}

async function requestForm<T>(path: string, form: FormData, _retried = false): Promise<T> {
  const token = getToken();
  const res = await fetch(buildApiUrl(path), {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      ...getClientHeaders(),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: form,
  });
  if (!res.ok) {
    let body: any = null;
    let text = '';
    try {
      const ct = res.headers.get('content-type') || '';
      if (ct.includes('application/json')) body = await res.json();
      else text = await res.text();
    } catch {}
    if (res.status === 401 && !_retried) {
      const newToken = await doRefresh();
      if (newToken) return requestForm<T>(path, form, true);
      await clearAuthStorage();
    }
    if (body && typeof body === 'object') {
      const code = body.error || body.code;
      const msg = body.message || code || res.statusText;
      throw new HttpError(res.status, msg, { code, rid: body.rid, details: body.details, body });
    }
    throw new HttpError(res.status, text || res.statusText);
  }
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) return res.json();
  // @ts-ignore
  return res.text();
}

async function request<T>(path: string, opts: RequestInit = {}, _retried = false, _retry429 = 0): Promise<T> {
  const method = String((opts.method || 'GET') as any).toUpperCase();
  const url = buildApiUrl(path);

  if (method === 'GET') {
    const key = url;
    const existing = inflightGet.get(key);
    if (existing) return existing as Promise<T>;
    const p = requestNoDedupe<T>(path, { ...opts, method: 'GET' }, _retried, _retry429).finally(() => {
      inflightGet.delete(key);
    });
    inflightGet.set(key, p as any);
    return p;
  }

  return requestNoDedupe<T>(path, opts, _retried, _retry429);
}

async function requestNoDedupe<T>(path: string, opts: RequestInit = {}, _retried = false, _retry429 = 0): Promise<T> {
  const token = getToken();
  const url = buildApiUrl(path);

  const res = await fetch(url, {
    credentials: 'include',
    cache: 'no-store',
    headers: { 
      'Content-Type': 'application/json', 
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      ...getClientHeaders(),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}) 
    },
    ...opts,
  });
  if (!res.ok) {
    if (res.status === 429 && _retry429 < 3) {
      const ra = res.headers.get('retry-after');
      const raMs = (() => {
        const n = ra ? Number(ra) : NaN;
        if (Number.isFinite(n) && n > 0) return Math.min(30_000, Math.round(n * 1000));
        return null;
      })();
      const backoffMs = raMs ?? Math.min(10_000, 500 * Math.pow(2, _retry429));
      await sleep(backoffMs);
      return requestNoDedupe<T>(path, opts, _retried, _retry429 + 1);
    }
    // Try to parse JSON error to preserve server error code and details
    let body: any = null;
    let text = '';
    try {
      const ct = res.headers.get('content-type') || '';
      if (ct.includes('application/json')) {
        body = await res.json();
      } else {
        text = await res.text();
      }
    } catch {}
    if (res.status === 401 && !_retried) {
      const newToken = await doRefresh();
      if (newToken) return requestNoDedupe<T>(path, opts, true);
      await clearAuthStorage();
    }
    if (body && typeof body === 'object') {
      const code = body.error || body.code;
      const msg = body.message || code || res.statusText;
      throw new HttpError(res.status, msg, { code, rid: body.rid, details: body.details, body });
    }
    throw new HttpError(res.status, text || res.statusText);
  }
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) return res.json();
  // @ts-ignore
  return res.text();
}

export const http = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: any) => request<T>(path, { method: 'POST', body: JSON.stringify(body || {}) }),
  put: <T>(path: string, body?: any) => request<T>(path, { method: 'PUT', body: JSON.stringify(body || {}) }),
  patch: <T>(path: string, body?: any) => request<T>(path, { method: 'PATCH', body: JSON.stringify(body || {}) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  uploadForm: async <T = any>(path: string, form: FormData) => requestForm<T>(path, form),
  upload: async <T = any>(path: string, files: File[]) => {
    const token = getToken();
    const form = new FormData();
    files.forEach(f => form.append('files', f));
    const res = await fetch(buildApiUrl(path), {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...getClientHeaders(),
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: form
    });
    if (!res.ok) {
      let body: any = null;
      let text = '';
      try {
        const ct = res.headers.get('content-type') || '';
        if (ct.includes('application/json')) body = await res.json();
        else text = await res.text();
      } catch {}
      if (body && typeof body === 'object') {
        const code = body.error || body.code;
        const msg = body.message || code || res.statusText;
        throw new HttpError(res.status, msg, { code, rid: body.rid, details: body.details, body });
      }
      throw new HttpError(res.status, text || res.statusText);
    }
    return res.json() as Promise<T>;
  },
  uploadWithProgress: async <T = any>(path: string, files: File[], onProgress?: (progress: { loaded: number; total: number; percent: number | null }) => void) => {
    return xhrUpload<T>(path, files, onProgress);
  },
  createUploadTask: <T = any>(path: string, files: File[], onProgress?: (progress: UploadProgress) => void) => {
    return xhrUploadTask<T>(path, files, onProgress);
  },
  openDownload: (url: string, filename?: string) => {
    const finalUrl = buildApiUrl(url);
    const a = document.createElement('a');
    a.href = finalUrl;
    if (filename) a.download = filename;
    a.rel = 'noopener';
    a.target = '_blank';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
  },
  downloadAndOpenChatFile: async (url: string, filename: string, chatName: string) => {
    const cleanedUrl = String(url || '').trim();
    if (!cleanedUrl) return false;
    try {
      if (typeof window !== 'undefined' && window.desktopApi?.downloadAndOpenFile) {
        const ok = await window.desktopApi.downloadAndOpenFile({ url: cleanedUrl, filename, chatName }).catch(() => false);
        if (ok) return true;
      }
    } catch {}
    try {
      const blob = await downloadBlob(cleanedUrl);
      await triggerBrowserDownload(blob, filename || 'file');
      try { playSoundEvent('download_complete'); } catch {}
      return true;
    } catch {
      return false;
    }
  },
  downloadChatFileSaveAs: async (url: string, filename: string, chatName: string) => {
    const cleanedUrl = String(url || '').trim();
    if (!cleanedUrl) return false;
    try {
      if (typeof window !== 'undefined' && window.desktopApi?.downloadFileSaveAs) {
        return await window.desktopApi.downloadFileSaveAs({ url: cleanedUrl, filename, chatName }).catch(() => false);
      }
    } catch {}
    try {
      const blob = await downloadBlob(cleanedUrl);
      await triggerBrowserDownload(blob, filename || 'file');
      try { playSoundEvent('download_complete'); } catch {}
      return true;
    } catch {
      return false;
    }
  },
  downloadToFile: async (url: string, filename: string) => {
    const blob = await downloadBlob(url);
    await triggerBrowserDownload(blob, filename);
    try { playSoundEvent('download_complete'); } catch {}
  },
};
